import type { Uuid } from "spacetimedb";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as v from "valibot";
import { usePostHog } from "@posthog/react";
import { PosthogEvents } from "@/web/lib/posthog-events";
import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Input } from "@/web/components/primitives/input";
import { toaster } from "@/web/components/primitives/toast";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/web/components/styled/Field";
import { formatCanonicalAddressShort } from "@/web/lib/addresses";
import { ErrorHandler } from "@/web/lib/errors";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import type { Property } from "@tycho/web-bindings/types";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

type InviteLandlordProps = {
  /** Underwritable entity that drives the email's tenant + property template vars. */
  underwritableEntity: Uuid;
  /** Every targeted property whose landlord hasn't been invited yet. */
  properties: Property[];
};

const inviteLandlordSchema = v.object({
  email: v.pipe(
    v.string(),
    v.trim(),
    v.minLength(1, "Email is required"),
    v.email("Please enter a valid email address"),
  ),
});

type InviteLandlordFormValues = v.InferOutput<typeof inviteLandlordSchema>;

function InviteLandlordAction({
  underwritableEntity,
  properties,
  onSkip,
  onComplete,
}: InviteLandlordProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);
  const multiple = properties.length > 1;

  return (
    <>
      <ActionCard category="negotiate">
        <ActionCard.Header>
          <ActionCard.Badge>Price</ActionCard.Badge>
          <ActionCard.DetailsTrigger />
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>
            {multiple ? "Invite your landlords" : "Invite your landlord"}
          </ActionCard.Title>
          <ActionCard.Description>
            {multiple
              ? "Bring the landlords for your properties into the process early to see if there's enough common ground to make a deal."
              : "Bring your landlord into the process early to see if there is enough common ground to make a deal."}
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>Invite</ActionCard.PrimaryButton>
        </ActionCard.Footer>
        <ActionCard.Back title="Why invite your landlord early?">
          <p>
            Looping in your landlord at the start of the process is the fastest way to figure out
            whether a deal is even viable. If you can't find common ground early, it's much less
            costly to pivot.
          </p>
        </ActionCard.Back>
      </ActionCard>

      <InviteLandlordDrawer
        open={open}
        underwritableEntity={underwritableEntity}
        properties={properties}
        onOpenChange={setOpen}
        onComplete={onComplete}
      />
    </>
  );
}

/**
 * Single card across every targeted property: a picker of uninvited properties
 * → that property's email form → back to the picker. The set of properties is
 * snapshotted when the drawer opens so sending an invite (which flips the live
 * `invitedLandlord` flag) doesn't reshuffle the list mid-session. The card
 * leaves the deck once all properties have an invite (rule predicate).
 */
function InviteLandlordDrawer({
  open,
  underwritableEntity,
  properties,
  onOpenChange,
  onComplete,
}: {
  open: boolean;
  underwritableEntity: Uuid;
  properties: Property[];
  onOpenChange: (open: boolean) => void;
  onComplete: (result?: unknown) => void;
}) {
  // Which property's form is showing, or null for the picker list.
  const [activeId, setActiveId] = useState<string | null>(null);
  // Locally track invites sent this session so the picker reflects progress
  // without depending on the live subscription mid-drawer.
  const [invitedIds, setInvitedIds] = useState<ReadonlySet<string>>(new Set());

  const single = properties.length === 1;
  const active = activeId ? properties.find((p) => p.id.toString() === activeId) : undefined;
  // For a single property, skip the picker and go straight to its form.
  const showForm = active ?? (single ? properties[0] : undefined);

  const handleSent = (propertyId: Uuid) => {
    const next = new Set(invitedIds);
    next.add(propertyId.toString());
    setInvitedIds(next);
    if (single || next.size >= properties.length) {
      onComplete();
      onOpenChange(false);
    } else {
      // Back to the picker to choose the next one.
      setActiveId(null);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          {showForm && !single ? (
            <button
              type="button"
              aria-label="Back"
              className="inline-flex size-5 items-center justify-center text-withus-dark-blue"
              onClick={() => setActiveId(null)}
            >
              <ChevronLeftIcon className="size-4" />
            </button>
          ) : (
            <span className="size-5" aria-hidden />
          )}
          <DrawerTitle>
            {showForm
              ? `Invite landlord — ${formatCanonicalAddressShort(showForm.address)}`
              : "Invite your landlords"}
          </DrawerTitle>
          <DrawerCloseIcon />
        </DrawerHeader>
        {showForm ? (
          <InviteLandlordDrawerBody
            key={showForm.id.toString()}
            underwritableEntity={underwritableEntity}
            targetableProperty={showForm.id}
            onSent={() => handleSent(showForm.id)}
          />
        ) : (
          <DrawerBody className="flex flex-col gap-3 py-6">
            <p className="text-sm text-muted-foreground">
              Choose a property to invite its landlord. You can do these in any order.
            </p>
            <ul className="flex flex-col gap-2">
              {properties.map((p) => {
                const done = invitedIds.has(p.id.toString());
                return (
                  <li key={p.id.toString()}>
                    <button
                      type="button"
                      disabled={done}
                      onClick={() => setActiveId(p.id.toString())}
                      className="flex w-full items-center justify-between border border-border p-4 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span className="font-heading text-base uppercase">
                        {formatCanonicalAddressShort(p.address)}
                      </span>
                      {done ? (
                        <span className="text-xs font-semibold tracking-wide text-withus-dark-blue/60 uppercase">
                          Invited
                        </span>
                      ) : (
                        <ChevronRightIcon className="size-4 text-withus-dark-blue/60" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </DrawerBody>
        )}
      </DrawerContent>
    </Drawer>
  );
}

function InviteLandlordDrawerBody({
  underwritableEntity,
  onSent,
  targetableProperty,
}: {
  underwritableEntity: Uuid;
  onSent: () => void;
  targetableProperty: Uuid;
}) {
  const posthog = usePostHog();
  const form = useForm<InviteLandlordFormValues>({
    resolver: valibotResolver(inviteLandlordSchema),
    defaultValues: { email: "" },
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const sendInvitationMutation = useProcedureMutation(
    procedures.sendLandlordNegotiationInvitation,
    {
      onSuccess: () => {
        PosthogEvents.LandlordInvited(posthog, underwritableEntity);
        toaster({ message: "Invitation sent", variant: "success" });
        onSent();
      },
      onError: ErrorHandler.default,
    },
  );

  // The landlord form lives at /landlord/<underwritableEntityId>. Built on the
  // client so the email links back to whichever environment (preview vs prod)
  // the user is actually using.
  const landlordFormUrl = `${window.location.origin}/landlord/${underwritableEntity.toString()}`;

  const onSubmit = form.handleSubmit(({ email }) => {
    sendInvitationMutation.mutate({
      to: email,
      underwritableEntity,
      targetableProperty,
      landlordFormUrl,
    });
  });

  const isSubmitDisabled = !form.formState.isValid || sendInvitationMutation.isPending;

  return (
    <>
      <DrawerBody>
        <form id="invite-landlord-form" onSubmit={onSubmit}>
          <FieldGroup>
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="invite-landlord-email">Landlord email</FieldLabel>
                  <Input
                    {...field}
                    id="invite-landlord-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="landlord@example.com"
                    aria-invalid={fieldState.invalid}
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
          </FieldGroup>
        </form>
      </DrawerBody>
      <DrawerFooter>
        <Button
          type="submit"
          form="invite-landlord-form"
          color="red"
          size="lg"
          disabled={isSubmitDisabled}
          isLoading={sendInvitationMutation.isPending}
        >
          {sendInvitationMutation.isPending ? "Sending…" : "Send invitation"}
        </Button>
      </DrawerFooter>
    </>
  );
}

export function createInviteLandlordAction(
  props: InviteLandlordProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "invite-landlord", canSkip = true, ...rest } = props;
  return {
    id,
    category: "negotiate",
    canSkip,
    render: (core) => <InviteLandlordAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createInviteLandlordCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/InviteLandlordCompleted";

// Single card across every targeted property: shows while ANY property has no
// landlord invited; its drawer picks each uninvited property in turn. Leaves the
// active deck once all properties have an invite.
//
// Hidden for invited brands.
export const inviteLandlordRule = CardRulesEngine.singletonRule({
  id: "invite-landlord",
  priority: 20,
  when: CardRulesEngine.and(
    (d) => d.properties.some((p) => CardRulesEngine.propertyHasNoLandlordInvited(d, p)),
    CardRulesEngine.not(CardRulesEngine.businessStoryIs("Invited")),
  ),
  build: (d) =>
    createInviteLandlordAction({
      underwritableEntity: d.underwritableEntityId,
      properties: d.properties.filter((p) => CardRulesEngine.propertyHasNoLandlordInvited(d, p)),
    }),
});

export const inviteLandlordCompletedRule = CardRulesEngine.forEachCompletedRule({
  id: "invite-landlord:done",
  forEach: CardRulesEngine.property,
  when: CardRulesEngine.and(
    CardRulesEngine.propertyHasLandlordInvited,
    CardRulesEngine.not(CardRulesEngine.businessStoryIs("Invited")),
  ),
  build: (_, p) => createInviteLandlordCompleted({ property: p }),
});
